import { Link } from "react-router-dom";
import { Facebook, BadgeCheck, Trophy, ShieldCheck, BookOpen } from "lucide-react";
import { CONTACT_INFO } from "../i18n";
import { useLang } from "../context/LanguageContext";
import { blT } from "../content/blacklistI18n";
import { EXTRA, COMPANY, VAT_NOTE } from "../content/extra";
import { MarketingContact } from "./MarketingContact";
import { FOOTER_LEGAL } from "../content/legalInfo";
import { TERMS } from "../content/termsContent";

const COPYRIGHT_NOTICE = {
  sk: "SauberEko® je registrovaná ochranná známka Európskej únie (EUIPO 019269791). Prevádzkovateľom tejto stránky je spoločnosť SauberEko s. r. o. — plne licencovaný držiteľ značky SauberEko®. Všetky práva vyhradené. Obsah tejto webovej stránky, vrátane textov, grafiky, log, ochranných známok a metodík (vrátane projektu „Pracuj, uč sa a zarábaj©“), je majetkom značky SauberEko®, resp. jej licencovaného držiteľa SauberEko s. r. o., alebo sa používa s povolením, a je chránený zákonom o autorských právach a ďalšími právnymi predpismi. Žiadna časť tejto stránky ani jej obsahu nesmie byť reprodukovaná, duplikovaná, kopírovaná, predaná, preposlaná ani inak využívaná na komerčné účely bez výslovného písomného súhlasu spoločnosti SauberEko s. r. o. Spoločnosť si vyhradzuje výhradné právo rozhodnúť o nadviazaní spolupráce.",
  en: "SauberEko® is a registered European Union trademark (EUIPO 019269791). This website is operated by SauberEko s. r. o. — a fully licensed holder of the SauberEko® brand. All rights reserved. The content of this website, including texts, graphics, logos, trademarks and methodologies (including the “Work, learn and earn©” project), is the property of the SauberEko® brand, or its licensed holder SauberEko s. r. o., or is used with permission, and is protected by copyright law and other legal regulations. No part of this website or its content may be reproduced, duplicated, copied, sold, resold or otherwise used for commercial purposes without the express written consent of SauberEko s. r. o. The company reserves the exclusive right to decide on entering into cooperation.",
  de: "SauberEko® ist eine eingetragene Unionsmarke (EUIPO 019269791). Betreiber dieser Website ist die SauberEko s. r. o. — ein vollständig lizenzierter Inhaber der Marke SauberEko®. Alle Rechte vorbehalten. Der Inhalt dieser Website, einschließlich Texte, Grafiken, Logos, Marken und Methodiken (einschließlich des Projekts „Arbeite, lerne und verdiene©“), ist Eigentum der Marke SauberEko® bzw. ihres lizenzierten Inhabers SauberEko s. r. o. oder wird mit Genehmigung verwendet und ist durch das Urheberrecht und weitere Rechtsvorschriften geschützt. Kein Teil dieser Website oder ihres Inhalts darf ohne die ausdrückliche schriftliche Zustimmung der SauberEko s. r. o. reproduziert, dupliziert, kopiert, verkauft, weitergeleitet oder anderweitig für kommerzielle Zwecke genutzt werden. Das Unternehmen behält sich das ausschließliche Recht vor, über die Aufnahme einer Zusammenarbeit zu entscheiden.",
  pl: "SauberEko® jest zarejestrowanym znakiem towarowym Unii Europejskiej (EUIPO 019269791). Operatorem tej strony jest spółka SauberEko s. r. o. — w pełni licencjonowany posiadacz marki SauberEko®. Wszelkie prawa zastrzeżone. Treść tej strony internetowej, w tym teksty, grafiki, logo, znaki towarowe i metodyki (w tym projekt „Pracuj, ucz się i zarabiaj©“), jest własnością marki SauberEko® lub jej licencjonowanego posiadacza SauberEko s. r. o., albo jest używana za zgodą i jest chroniona prawem autorskim oraz innymi przepisami. Żadna część tej strony ani jej treści nie może być powielana, duplikowana, kopiowana, sprzedawana, przesyłana ani w inny sposób wykorzystywana do celów komercyjnych bez wyraźnej pisemnej zgody spółki SauberEko s. r. o. Spółka zastrzega sobie wyłączne prawo do decydowania o nawiązaniu współpracy.",
  fr: "SauberEko® est une marque de l'Union européenne enregistrée (EUIPO 019269791). Ce site est exploité par la société SauberEko s. r. o. — titulaire pleinement licencié de la marque SauberEko®. Tous droits réservés. Le contenu de ce site web, y compris les textes, graphiques, logos, marques et méthodologies (dont le projet « Travaille, apprends et gagne© »), est la propriété de la marque SauberEko® ou de son titulaire licencié SauberEko s. r. o., ou est utilisé avec autorisation, et est protégé par le droit d'auteur et d'autres dispositions légales. Aucune partie de ce site ni de son contenu ne peut être reproduite, dupliquée, copiée, vendue, retransmise ou autrement utilisée à des fins commerciales sans le consentement écrit exprès de la société SauberEko s. r. o. La société se réserve le droit exclusif de décider de l'établissement d'une coopération.",
};

export const Footer = () => {
  const { t, lang } = useLang();
  const ex = EXTRA[lang];

  const legal = [
    [ex.company.labels.director, COMPANY.director],
    [ex.company.labels.ico, COMPANY.ico],
    [ex.company.labels.dic, COMPANY.dic],
    [ex.company.labels.icdph, `${COMPANY.icdph}`],
    [ex.company.labels.register, COMPANY.register],
  ];

  return (
    <footer
      data-testid="footer"
      className="relative z-10 bg-[#050505] border-t border-white/10 pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-10 pb-12">
          <div className="md:col-span-2">
            <p className="font-display text-3xl font-semibold">
              Sauber<span className="gold-text">Eko</span>
              <sup className="text-[#D4AF37] text-xs ml-0.5">®</sup>
            </p>
            <p className="text-white/50 mt-4 max-w-xs leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="flex items-start gap-3 mt-5 max-w-md">
              <BadgeCheck size={18} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.5} />
              <p data-testid="footer-brand-note" className="text-white/45 text-xs leading-relaxed">
                {ex.brandNote}
              </p>
            </div>
            <p data-testid="footer-vat-note" className="text-white/35 text-xs leading-relaxed mt-4 max-w-md">
              {VAT_NOTE[lang]}
            </p>
          </div>

          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-4">
              {t.nav.services}
            </p>
            <nav className="flex flex-col gap-2">
              <Link to="/services" className="text-white/70 hover:text-[#D4AF37] transition-colors">{t.nav.services}</Link>
              <Link to="/certification" className="text-white/70 hover:text-[#D4AF37] transition-colors">{t.nav.certification}</Link>
              <Link to="/standard" className="text-white/70 hover:text-[#D4AF37] transition-colors">{t.nav.standard}</Link>
              <Link to="/pricing" className="text-white/70 hover:text-[#D4AF37] transition-colors">{ex.nav.pricing}</Link>
              <Link to="/cierna-listina" data-testid="footer-blacklist-link" className="text-white/70 hover:text-[#D4AF37] transition-colors">{blT(lang).title}</Link>
              <Link to="/about" className="text-white/70 hover:text-[#D4AF37] transition-colors">{t.nav.about}</Link>
              <Link to="/metodika" data-testid="footer-metodika-link" className="inline-flex items-center gap-1.5 text-white/70 hover:text-[#D4AF37] transition-colors">
                <BookOpen size={14} /> {({ sk: "Pracuj, uč sa a zarábaj©", en: "Work, learn and earn©", de: "Arbeite, lerne und verdiene©", pl: "Pracuj, ucz się i zarabiaj©", fr: "Travaille, apprends et gagne©" })[lang] || "Pracuj, uč sa a zarábaj©"}
              </Link>
              <Link to="/pre-profesionalov" data-testid="footer-pre-profesionalov-link" className="text-white/70 hover:text-[#D4AF37] transition-colors">
                {({ sk: "Buď v databáze SauberEko®", en: "Join the SauberEko® database", de: "In die SauberEko®-Datenbank", pl: "Bądź w bazie SauberEko®", fr: "Rejoindre la base SauberEko®" })[lang] || "Buď v databáze SauberEko®"}
              </Link>
              <Link to="/hall-of-fame" className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#F2E3C6] transition-colors">
                <Trophy size={14} /> {ex.hallOfFame.nav}
              </Link>
              <Link to="/about#euipo" className="inline-flex items-center gap-1.5 text-white/70 hover:text-[#D4AF37] transition-colors">
                <ShieldCheck size={14} /> {ex.euipo.button}
              </Link>
            </nav>
          </div>

          <div data-testid="footer-contact-info">
            <p className="text-xs tracking-[0.2em] uppercase text-white/40 mb-4">
              {t.nav.contact}
            </p>
            <a href={`mailto:${CONTACT_INFO.email}`} className="block text-white/80 hover:text-[#D4AF37] transition-colors">
              {CONTACT_INFO.email}
            </a>
            <a href={`tel:${CONTACT_INFO.phoneRaw}`} className="block text-white/80 hover:text-[#D4AF37] transition-colors mt-2">
              {CONTACT_INFO.phone}
            </a>
            <p className="text-white/50 mt-2 text-sm">{COMPANY.address}</p>
            <a
              href={CONTACT_INFO.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-facebook"
              className="inline-flex items-center justify-center w-11 h-11 border border-white/15 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors mt-5"
            >
              <Facebook size={18} />
            </a>
            <MarketingContact />
          </div>
        </div>

        {/* Legal identifiers */}
        <div className="hairline" />
        <div data-testid="footer-legal" className="flex flex-wrap gap-x-8 gap-y-2 pt-6 text-xs text-white/40">
          <span className="text-white/60">{COMPANY.name}</span>
          {legal.map(([label, value], i) => (
            <span key={i}>
              <span className="uppercase tracking-wider">{label}:</span> {value}
            </span>
          ))}
        </div>

        <div data-testid="footer-legal-info" className="mt-6 max-w-4xl text-[11px] leading-relaxed text-white/40">
          <p className="text-white/60 uppercase tracking-wider text-xs mb-1.5">{(FOOTER_LEGAL[lang] || FOOTER_LEGAL.sk).title}</p>
          <p>{(FOOTER_LEGAL[lang] || FOOTER_LEGAL.sk).text}{" "}
            <Link to="/privacy" data-testid="footer-legal-privacy-link" className="text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2">
              {(FOOTER_LEGAL[lang] || FOOTER_LEGAL.sk).link}
            </Link>
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 mt-6 border-t border-white/10 text-sm text-white/40">
          <p>© {new Date().getFullYear()} SauberEko® — {COMPANY.name}. {t.footer.rights}</p>
          <div className="flex items-center gap-6">
            <Link to="/obchodne-podmienky" data-testid="footer-terms-link" className="hover:text-[#D4AF37] transition-colors">
              {(TERMS[lang] || TERMS.sk).nav}
            </Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#D4AF37] transition-colors">
              {t.footer.privacy}
            </Link>
          </div>
        </div>

        <p
          data-testid="footer-copyright-notice"
          className="text-white/30 text-[11px] leading-relaxed mt-6 max-w-4xl"
        >
          {COPYRIGHT_NOTICE[lang] || COPYRIGHT_NOTICE.sk}
        </p>
      </div>
    </footer>
  );
};
